import {
    Alignment, ArrayUtil, Collection, Color, DOMUtil, HorizontalAlignment, Logger, LogManager, VerticalAlignment
} from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { AllowedToSearch } from "../../databinding/AllowedToSearch";
import { Button } from "../button/Button";
import { Component } from "../../base/Component";
import { ComponentDeserializer, DeserializeProps } from "../../serializer/ComponentDeserializer";
import { ComponentPropDefinition, ComponentPropDefinitions } from "../../base/ComponentProps";
import { ComponentTypes } from "../../base/ComponentTypes";
import { Event } from "../../events/Event";
import { Image } from "../image/Image";
import { Label } from "../label/Label";
import { LabelVariant } from "../label/LabelVariant";
import { ListenerListDef } from "../../base/ListenerListDef";
import { Panel } from "../panel/Panel";
import { SelectionListener } from "../../events/SelectionEvent";
import { serializeComponents } from "../../serializer/ComponentSerializer";
import { TabCloseEvent, TabCloseListener } from "../../events/TabCloseEvent";
import { TabPropDefinitions, TabProps } from "./TabProps";
import { TabSelectorPanel } from "./TabSelectorPanel";
import { TabSelectorPanelAccordion } from "./TabSelectorPanelAccordion";
import { TabSelectorPanelScroll } from "./TabSelectorPanelScroll";
import { TabSelectorPanelOutlined } from "./TabSelectorPanelOutlined";
import { TabSelectorPanelUnderlined } from "./TabSelectorPanelUnderlined";
import { Tabset } from "./Tabset";
import { TabsetPropDefinitions, TabStyle } from "./TabsetProps";
import { TooltipContent } from "../../page/tooltips/TooltipContent";

const _beforeSelectionListenerDef: ListenerListDef = { listName: "_beforeSelectionListeners" };
const _afterSelectionListenerDef: ListenerListDef = { listName: "_afterSelectionListeners" };
const _beforeCloseListenerDef: ListenerListDef = { listName: "_beforeCloseListeners" };
const _afterCloseListenerDef: ListenerListDef = { listName: "_afterCloseListeners" };

enum InnerIds {
    LABEL_SCROLL_TITLE = "autoTitleLabel",
    PANEL_ADDL_TITLE_COMPS = "addlTitleCompsPanel"
}

export class Tab extends Panel implements TabProps {
    private static log: Logger;
    private _caption: string;
    private _displaysDividerBorder: boolean;
    private _heading: TabSelectorPanel;
    private _titleLabel: Label;
    private _addlTitleCompsPanel: Panel;
    private _addlTitleComps: Component[];
    private _imageColor: Color;
    private _imageFill: string;
    private _imageHeight: string | number;
    private _imageName: ImageName;
    private _imageRotation: number;
    private _imageStroke: string;
    private _imageStrokeWidth: number;
    private _imageWidth: string | number;
    private _closeable: boolean;
    _index: number;
    private _tabStyle: TabStyle;
    private _tabset: Tabset;
    private _selected: boolean;
    private _showTitleInScroll: boolean = true;
    private _calculatedHeight: string | number;
    private _headingMaxWidth: number;
    private _headingMinWidth: number;

    constructor(props?: Partial<TabProps>) {
        super({ fillRow: true });
        this.setProps(props);
        this.supportsEditSecurity = true;
    }

    override syncInnerComponentIds() {
        this.setInnerComponentId(this._heading, "tabHeading");
        this._heading?.syncInnerComponentIds();
        this.setInnerComponentId(this._titleLabel, InnerIds.LABEL_SCROLL_TITLE);
        this.setInnerComponentId(this._addlTitleCompsPanel, InnerIds.PANEL_ADDL_TITLE_COMPS);
    }

    override setProps(props: Partial<TabProps>) {
        super.setProps(props);
    }

    get caption(): string {
        return this._caption;
    }

    set caption(value: string) {
        const oldValue = this._caption;
        this._caption = value;
        this.heading?.syncLabel();
        this.heading?.syncWidth();
        if (this._titleLabel != null) {
            this._titleLabel.text = value;
        }
        this._matchIdToValue(oldValue, value);
    }

    get displaysDividerBorder(): boolean {
        return this.tabStyle === TabStyle.UNDERLINED &&
            this.isVertical() &&
            (this._displaysDividerBorder ?? this.getPropertyDefinitions().displaysDividerBorder.defaultValue) &&
            ArrayUtil.getLastElement(this.tabset.components) !== this;
    }

    set displaysDividerBorder(value: boolean) {
        this._displaysDividerBorder = value;
    }

    syncDividerBorder() {
        if (this.heading instanceof TabSelectorPanelUnderlined) {
            this.heading.syncBorder();
        }
    }

    public override get tooltip(): TooltipContent {
        return super.tooltip;
    }

    public override set tooltip(value: TooltipContent) {
        super.tooltip = value;
        if (this.headingLabel != null) {
            this.headingLabel.tooltip = value;
        }
    }

    public override get tooltipDynamicContent(): TooltipContent {
        return super.tooltipDynamicContent;
    }

    public override set tooltipDynamicContent(value: TooltipContent) {
        super.tooltipDynamicContent = value;
        if (this.headingLabel != null) {
            this.headingLabel.tooltipDynamicContent = value;
        }
    }

    override get parent(): Tabset {
        return this._tabset;
    }

    override set parent(value: Tabset) { }

    get tabset(): Tabset {
        return this._tabset
    }

    set tabset(value: Tabset) {
        this._tabset = value;
        if (this.tabset != null && this.tabset.tabStyle !== this.tabStyle)
            this.tabStyle = this.tabset.tabStyle;
        this.createTitle();
    }

    get closeable() {
        return this._closeable ?? this.getPropertyDefinitions().closeable.defaultValue;
    }

    set closeable(value) {
        if (this._closeable === value) {
            return;
        }
        this._closeable = value;
        this.heading?.syncCloseable();
    }

    select(event?: Event) {
        if (this._selected === true && this.tabStyle !== TabStyle.SCROLL)
            return;
        this._handleTabGrowing();
        this._tabset._setSelectedInternal(this._index, true, event?.domEvent);
    }

    public selectAndScroll(event?: Event) {
        this.select(event);
        if (this.tabStyle === TabStyle.SCROLL) {
            this.scrollTo();
        }
    }

    private scrollTo() {
        AllowedToSearch.add(this.tabset, this);
        this._tabset.scrollToTab(this);
    }

    private _handleTabGrowing() {
        if (this._designer != null)
            return;
        const tabGrew = this._tabset.growLastTab(this);
        if (tabGrew === true)
            this.growChildComponent(this._tabset.getContentHeight());
    }

    close(originatingEvent?: Event) {
        const beforeCloseEvent = this.fireListeners<TabCloseEvent>(_beforeCloseListenerDef, () => new TabCloseEvent(this, originatingEvent?.domEvent));
        this._internalClose(beforeCloseEvent, originatingEvent);
    }

    private _internalClose(beforeCloseEvent: TabCloseEvent, originatingEvent?: Event) {
        if (beforeCloseEvent != null && beforeCloseEvent.defaultPrevented)
            return;
        this.parent.remove(this);
        this.fireListeners(_afterCloseListenerDef, () => new TabCloseEvent(this, originatingEvent?.domEvent));
    }

    get heading(): TabSelectorPanel {
        return this._heading;
    }

    private set heading(value: TabSelectorPanel) {
        this._heading = value;
    }

    get headingImage(): Image {
        return this.heading?.image;
    }

    get headingLabel(): Label {
        return this.heading?.label;
    }

    public get headingMinWidth(): number {
        const minWidthValue = this._headingMinWidth ?? this.tabset?.tabHeadingMinWidth;
        if (minWidthValue != null) {
            return minWidthValue;
        }
        if (this.headingMaxWidth != null) {
            return Math.min(this.headingMaxWidth, this.defaultHeadingMinWidth);
        }
        return this.defaultHeadingMinWidth;
    }

    public set headingMinWidth(value: number) {
         // A string value can be provided from a serialized layout
        this._headingMinWidth = value != null ? Number(value) : null;
        this.heading?.syncWidth();
    }

    get defaultHeadingMinWidth(): number {
        return 160;
    }

    public get headingMaxWidth(): number {
        return this._headingMaxWidth ?? this.tabset?.tabHeadingMaxWidth;
    }

    public set headingMaxWidth(value: number) {
         // A string value can be provided from a serialized layout
        this._headingMaxWidth = value != null ? Number(value) : null;
        this.heading?.syncWidth();
    }

    get showTitleInScroll(): boolean {
        return this._showTitleInScroll;
    }

    set showTitleInScroll(value: boolean) {
        this._showTitleInScroll = value;
        this.createTitle();
    }

    get tabStyle(): TabStyle {
        return this._tabStyle || TabStyle.UNDERLINED;
    }

    set tabStyle(value: TabStyle) {
        this._tabStyle = value;
        this.createSelector();
    }

    public isAccordionTab(): boolean {
        return this.tabStyle === TabStyle.ACCORDION;
    }

    public isOutlinedTab(): boolean {
        return this.tabStyle === TabStyle.OUTLINED;
    }

    public isScrollTab(): boolean {
        return this.tabStyle === TabStyle.SCROLL;
    }

    public isUnderlinedTab(): boolean {
        return this.tabStyle === TabStyle.UNDERLINED;
    }

    createSelector() {
        // Since the selector panel can be recreated, we need to handle any extra, ad-hoc components that were added to
        // the selector panel.  Grab them and then add them to the new selector panel after it's been created.
        const existingHeaderAdhocComponents = this.heading?.adhocComponents ?? [];
        switch (this.tabStyle) {
            case TabStyle.SCROLL:
                this.heading = new TabSelectorPanelScroll(this);
                break;
            case TabStyle.ACCORDION:
                this.heading = new TabSelectorPanelAccordion(this);
                break;
            case TabStyle.UNDERLINED:
                this.heading = new TabSelectorPanelUnderlined(this);
                break;
            case TabStyle.OUTLINED:
                this.heading = new TabSelectorPanelOutlined(this);
                break;
            default:
                break;
        }
        existingHeaderAdhocComponents.forEach(component => {
            this.heading.insertBefore(component, this.heading.insertComponentsBeforeThis);
        })
    }

    public get tabAlignment(): Alignment {
        return this._tabset?.tabAlignment ?? TabsetPropDefinitions.getDefinitions().tabAlignment.defaultValue;
    }

    public isVertical() {
        return this._tabset?.isVertical() ?? false;
    }

    tabAlignmentIsBottom(): boolean {
        return this.tabAlignment === Alignment.BOTTOM;
    }

    tabAlignmentIsLeft(): boolean {
        return this.tabAlignment === Alignment.LEFT;
    }

    tabAlignmentIsRight(): boolean {
        return this.tabAlignment === Alignment.RIGHT;
    }

    tabAlignmentIsTop(): boolean {
        return this.tabAlignment === Alignment.TOP;
    }

    createTitle() {
        this.remove(this._titleLabel);
        if (this._showTitleInScroll !== true || this._tabset.usingScrollStyle() !== true)
            return;
        this._titleLabel = new Label({
            variant: LabelVariant.sectionHeading,
            id: this.createInnerComponentId(InnerIds.LABEL_SCROLL_TITLE),
            text: this.caption,
            rowBreak: false
        });
        this._titleLabel.deserialized = false;
        this.insert(this._titleLabel, 0);

        //if the layout didn't contain the addl title panel (that can accommodate additional components at the
        //same level as the title), add it (mainly so that users can add components in the designer).
        //remember, this only works when using the 'scroll' tab style and when we are showing the dynamically added title
        this.remove(this._addlTitleCompsPanel);
        if (ArrayUtil.isEmptyArray(this._addlTitleComps) !== true || this._designer != null) {
            this.createTitleComponentsPanel();
            this.insert(this._addlTitleCompsPanel, 1);
        }
        else {
            this._titleLabel.fillRow = true;
            this._titleLabel.rowBreak = true;
        }
    }

    createTitleComponentsPanel() {
        this._addlTitleCompsPanel = new Panel({
            id: this.createInnerComponentId(InnerIds.PANEL_ADDL_TITLE_COMPS),
            align: HorizontalAlignment.LEFT,
            verticalAlign: VerticalAlignment.CENTER,
            fillRow: true,
            rowBreak: true,
            margin: 0,
            padding: 0,
        });
        this._addlTitleCompsPanel.setDesigner(this._designer);
        this._addlTitleCompsPanel.deserialized = false;
        if (this._addlTitleComps != null) {
            for (const addlTitleComp of this._addlTitleComps)
                this._addlTitleCompsPanel.add(addlTitleComp);
        }
    }

    protected get buttonExpand(): Button {
        if (this.isAccordionTab()) {
            return (this.heading as TabSelectorPanelAccordion).buttonExpand;
        }
        return null;
    }

    get expanded(): boolean {
        if (this.isAccordionTab()) {
            return (this.heading as TabSelectorPanelAccordion).expanded;
        }
        return false;
    }

    set expanded(value: boolean) {
        if (this.isAccordionTab()) {
            (this.heading as TabSelectorPanelAccordion).expanded = value;
        }
    }

    expandCollapseTab(value: boolean, animate: boolean) {
        if (this.isAccordionTab()) {
            (this.heading as TabSelectorPanelAccordion).expandCollapseTab(value, animate);
        }
    }

    toggleExpanded() {
        if (this.isAccordionTab()) {
            (this.heading as TabSelectorPanelAccordion).toggleExpanded();
        }
    }

    get imageColor(): Color {
        return this._imageColor;
    }

    set imageColor(value: Color) {
        if (this.imageColor === value) {
            return;
        }
        this._imageColor = value;
        this.heading?.syncImage();
    }

    get imageFill(): string{
        return this._imageFill;
    }

    set imageFill(value: string) {
        if (this.imageFill === value) {
            return;
        }
        this._imageFill = value;
        this.heading?.syncImage();
    }

    get imageHeight(): string | number {
        return this._imageHeight;
    }

    set imageHeight(value: string | number) {
        if (this.imageHeight === value) {
            return;
        }
        this._imageHeight = value;
        this.heading?.syncImage();
    }

    get imageName(): ImageName {
        return this._imageName;
    }

    set imageName(value: ImageName) {
        if (this.imageName === value) {
            return;
        }
        this._imageName = value;
        this.heading?.syncImage();
    }

    get imageRotation(): number {
        return this._imageRotation;
    }

    set imageRotation(value: number) {
        if (this.imageRotation === value) {
            return;
        }
        this._imageRotation = value;
        this.heading?.syncImage();
    }

    get imageStroke(): string {
        return this._imageStroke;
    }

    set imageStroke(value: string) {
        if (this.imageStroke === value) {
            return;
        }
        this._imageStroke = value;
        this.heading?.syncImage();
    }

    get imageStrokeWidth(): number {
        return this._imageStrokeWidth;
    }

    set imageStrokeWidth(value: number) {
        if (this.imageStrokeWidth === value) {
            return;
        }
        this._imageStrokeWidth = value;
        this.heading?.syncImage();
    }

    get imageWidth(): string | number {
        return this._imageWidth;
    }

    set imageWidth(value: string | number) {
        if (this._imageWidth === value) {
            return;
        }
        this._imageWidth = value;
        this.heading?.syncImage();
    }

    addBeforeSelectionListener(listener: SelectionListener) {
        this.addEventListener(_beforeSelectionListenerDef, listener);
    }

    removeBeforeSelectionListener(listener: SelectionListener) {
        this.removeEventListener(_beforeSelectionListenerDef, listener);
    }

    addAfterSelectionListener(listener: SelectionListener) {
        this.addEventListener(_afterSelectionListenerDef, listener);
    }

    removeAfterSelectionListener(listener: SelectionListener) {
        this.removeEventListener(_afterSelectionListenerDef, listener);
    }

    addBeforeCloseListener(listener: TabCloseListener) {
        this.addEventListener(_beforeCloseListenerDef, listener);
    }

    removeBeforeCloseListener(listener: TabCloseListener) {
        this.removeEventListener(_beforeCloseListenerDef, listener);
    }

    addAfterCloseListener(listener: TabCloseListener) {
        this.addEventListener(_afterCloseListenerDef, listener);
    }

    removeAfterCloseListener(listener: TabCloseListener) {
        this.removeEventListener(_afterCloseListenerDef, listener);
    }

    get selected(): boolean {
        return this._selected;
    }

    set selected(value: boolean) {
        this._selected = value;
        this.heading?.syncStyle();
    }

    override getPropertyDefinitions(): ComponentPropDefinitions {
        return TabPropDefinitions.getDefinitions();
    }

    public override setDesigner(designer: any) {
        super.setDesigner(designer);
        this._heading?.setDesigner(this._designer);
    }

    override get serializationName() {
        return "tab";
    }

    override get properName(): string {
        return "Tab";
    }

    override growChildComponent(height: number) {
        let availableHeight = height;
        availableHeight -= DOMUtil.getStyleAttrAsNumber(DOMUtil.getComputedStyle("paddingTop", this._element));
        availableHeight -= DOMUtil.getStyleAttrAsNumber(DOMUtil.getComputedStyle("paddingBottom", this._element));
        availableHeight -= DOMUtil.getStyleAttrAsNumber(DOMUtil.getComputedStyle("marginTop", this._element));
        availableHeight -= DOMUtil.getStyleAttrAsNumber(DOMUtil.getComputedStyle("marginBottom", this._element));
        let compToGrow: Component;
        for (const childComp of this._components) {
            if (childComp.deserialized !== true) {
                availableHeight -= DOMUtil.getStyleAttrAsNumber(DOMUtil.getComputedStyle("paddingTop", childComp._element));
                availableHeight -= DOMUtil.getStyleAttrAsNumber(DOMUtil.getComputedStyle("paddingBottom", childComp._element));
                availableHeight -= DOMUtil.getStyleAttrAsNumber(DOMUtil.getComputedStyle("marginTop", childComp._element));
                availableHeight -= DOMUtil.getStyleAttrAsNumber(DOMUtil.getComputedStyle("marginBottom", childComp._element));
                continue;
            }
            if (compToGrow != null)
                return;
            compToGrow = childComp;
        }
        if (compToGrow != null)
            compToGrow.growToContainerHeight(availableHeight);
    }

    /**
     * Don't serialize the auto-generated title label
     */
    _serializeNonProps(): string {
        if (this.components == null)
            return "";
        const compsToSerialize = [];
        const addlTitleComps = [];
        for (const comp of this._components) {
            if (comp instanceof Label && comp.getUnprefixedId() === InnerIds.LABEL_SCROLL_TITLE)
                continue;
            if (comp instanceof Panel && comp.getUnprefixedId() === InnerIds.PANEL_ADDL_TITLE_COMPS) {
                for (const c of comp.components)
                    addlTitleComps.push(c);
                continue;
            }
            compsToSerialize.push(comp);
        }
        if (compsToSerialize.length === 0)
            return "";
        let result = "\"components\": " + serializeComponents(compsToSerialize, null,) + ",\n";
        if (ArrayUtil.isEmptyArray(addlTitleComps) !== true) {
            result += "\"addlTitleComps\": " + serializeComponents(addlTitleComps, null,) + ",\n";
        }
        return result;
    }

    override async _deserializeSpecialProps(deserializeProps: DeserializeProps): Promise<string[]> {
        const props = { ...deserializeProps };
        delete props.defaultPropValues.tabStyle;
        const result = super._deserializeSpecialProps(props);
        const compDef = props.def;
        if (Array.isArray(compDef.addlTitleComps) && ArrayUtil.isEmptyArray(compDef.addlTitleComps) !== true) {
            this._addlTitleComps = await new ComponentDeserializer({
                ...props,
                def: compDef.addlTitleComps,
                designer: this._designer
            }).deserialize();
        }
        return result;
    }

    /**
     * After deserializing, if the component list somehow included the auto-generated title label, set the member variable to reference that component.
     * This shouldn't happen since we don't serialize the title label in the designer, but it's worth checking in case it does.
     */
    override doAfterDeserialize() {
        if (this._components != null) {
            for (const comp of this._components) {
                if (comp instanceof Label && comp.getUnprefixedId() === InnerIds.LABEL_SCROLL_TITLE)
                    this._titleLabel = comp;
            }
        }
    }

    protected override determinePropertyDefaultValue(prop: ComponentPropDefinition): any {
        if (prop.name === "padding" && this.tabStyle === TabStyle.SCROLL)
            return 16;
        if (prop.name === "paddingTop" && this.tabStyle === TabStyle.SCROLL)
            return 24;
        if (prop.name === "height")
            return this._calculatedHeight;
        if (prop.name === "headingMinWidth")
            return this.tabset?.tabHeadingMinWidth;
        return super.determinePropertyDefaultValue(prop);
    }

    override getListenerDefs(): Collection<ListenerListDef> {
        return {
            ...super.getListenerDefs(),
            "beforeSelection": { ..._beforeSelectionListenerDef },
            "afterSelection": { ..._afterSelectionListenerDef },
            "beforeClose": { ..._beforeCloseListenerDef },
            "afterClose": { ..._afterCloseListenerDef }
        };
    }

    override _syncVisible() {
        super._syncVisible();
        if (this.heading != null)
            this.heading.visible = this.visible;
        this._tabset?.syncTabSelectorWidth();
        this._tabset?.refreshUnderline();
    }

    selectInDesigner() {
        if (this._designer != null)
            this._designer.selectComponent(this);
    }

    public setCalculatedHeight(value: string | number) {
        this._calculatedHeight = value;
        super.height = value;
    }

    override displayInViewMode() {
        super.displayInViewMode();
        if (this._titleLabel != null) {
            this._titleLabel.enabled = true;
        }
    }

    private getLog(): Logger {
        if (Tab.log == null) {
            Tab.log = LogManager.getLogger("components.tabset.Tab");
        }
        return Tab.log;
    }
}

ComponentTypes.registerComponentType("tab", {
    constructor: Tab.prototype.constructor
});
